import { useState } from 'react'
import { submitOverride } from '../api'

interface Props {
  context: {
    target_type: 'lep' | 'dcp'
    lga_slug: string
    zone?: string
    housing_form?: string
    field_key: string
    current_value: unknown
  }
  onClose: () => void
}

export function ReportError({ context, onClose }: Props) {
  const [proposed, setProposed] = useState('')
  const [clause, setClause] = useState('')
  const [text, setText] = useState('')
  const [email, setEmail] = useState('')
  const [state, setState] = useState<'idle' | 'sending' | 'sent' | 'error'>('idle')
  const [error, setError] = useState<string | null>(null)

  async function send() {
    setState('sending')
    try {
      const parsed = parseValue(proposed)
      await submitOverride({
        target_type: context.target_type,
        lga_slug: context.lga_slug,
        zone: context.zone ?? null,
        housing_form: context.housing_form ?? null,
        field_key: context.field_key,
        proposed_value: parsed,
        source_clause: clause || null,
        source_text: text || null,
        submitter_email: email || null,
      })
      setState('sent')
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
      setState('error')
    }
  }

  const inputCls =
    'w-full border border-massing-rule bg-massing-paper-deep px-2.5 py-1.5 mb-2 text-sm focus:outline-none focus:border-massing-highlight'

  if (state === 'sent') {
    return (
      <div className="mt-2 p-3 bg-massing-cream border border-massing-rule text-sm text-massing-ink">
        Thanks — your correction is queued for admin review.
        <button onClick={onClose} className="ml-3 brand-eyebrow hover:text-massing-maroon">close</button>
      </div>
    )
  }

  return (
    <div className="mt-2 p-4 bg-massing-paper-deep border border-massing-rule text-sm">
      <div className="brand-eyebrow mb-2">Report error — {context.field_key}</div>
      <div className="text-xs text-massing-ink-light mb-3">
        Current: {String(context.current_value ?? '—')} · LGA {context.lga_slug} · {context.zone}
        {context.housing_form ? ` · ${context.housing_form}` : ''}
      </div>
      <input className={inputCls} placeholder="Correct value (number or text)" value={proposed} onChange={(e) => setProposed(e.target.value)} />
      <input className={inputCls} placeholder="Source clause (e.g. 4.4(2A))" value={clause} onChange={(e) => setClause(e.target.value)} />
      <textarea className={inputCls} placeholder="Verbatim source text (helps reviewer)" rows={3} value={text} onChange={(e) => setText(e.target.value)} />
      <input className={inputCls} placeholder="Your email (optional)" value={email} onChange={(e) => setEmail(e.target.value)} />
      {error && <div className="text-xs text-massing-maroon mb-2">{error}</div>}
      <div className="flex gap-3 items-center">
        <button onClick={send} disabled={state === 'sending' || !proposed} className="bg-massing hover:bg-massing-deep text-massing-cream px-4 py-1.5 disabled:opacity-50">
          {state === 'sending' ? 'Sending…' : 'Submit correction'}
        </button>
        <button onClick={onClose} className="brand-eyebrow text-massing-ink-light hover:text-massing-maroon">cancel</button>
      </div>
    </div>
  )
}

function parseValue(s: string): unknown {
  const n = Number(s)
  return Number.isFinite(n) && s.trim() !== '' ? n : s
}
